import React, { useEffect } from 'react';

import {
  Button,
  Form,
  Col,
  Row,
  DatePicker,
} from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import Collapsed from 'shared/Collapsed/Collapsed';
import { InputStr } from 'shared/FormItems';

import { useTimezone, type Role } from 'features/user';
import { SelectUserRole } from 'features/users';

import './UsersFilter.scss';

type FormValues = {
  id?: number;
  email?: string;
  phone?: string;
  name?: string;
  role?: Role;
  createdFrom?: dayjs.Dayjs;
};

export type FilterValues = {
  id?: number;
  email?: string;
  phone?: string;
  name?: string;
  role?: Role;
  createdFrom?: string;
};

type Props = {
  userId?: number | null;
  onSubmit: (data: FilterValues) => void;
  onReset?: () => void;
};

export const UsersFilter = ({ onSubmit, onReset, userId }: Props) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'filters' });
  const [form] = Form.useForm();

  const handleResetClick = () => {
    form.resetFields();
    onReset?.();
  };

  useEffect(() => {
    form.setFieldValue('id', userId);
  }, [userId]);

  const { userTimezone } = useTimezone(handleResetClick);

  const onFinish = (values: FormValues) => {
    const filteredValues = Object.keys(values).reduce((acc, key) => {
      if (values[key as keyof FormValues] !== null) {
        return {
          ...acc,
          [key as keyof FormValues]: values[key as keyof FormValues],
        };
      }
      return acc;
    }, {});

    const valuesCopy: FilterValues = {
      ...filteredValues,
      createdFrom: values.createdFrom?.utcOffset(userTimezone).startOf('day').toISOString(),
    };

    onSubmit(valuesCopy);
  };

  return (
    <Collapsed label={t('filter', 'Фильтр')}>
      <Form
        layout="vertical"
        form={form}
        className="filter-form"
        onFinish={onFinish}
      >
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col xs={24} md={12} lg={8}>
            <InputStr
              type="email"
              formProps={{ className: 'filter-form__item' }}
            />
            <InputStr
              type="phone"
              formProps={{ className: 'filter-form__item' }}
            />
            <SelectUserRole className="filter-form__item" />
          </Col>
          <Col xs={24} md={12} lg={8}>
            <InputStr
              type="name"
              formProps={{ className: 'filter-form__item' }}
            />
            <InputStr
              type="phone"
              formProps={{ className: 'filter-form__item' }}
            />
          </Col>
          <Col xs={24} md={12} lg={8}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  className="filter-form__item "
                  label={t('createdFrom')}
                  name={'createdFrom'}
                >
                  <DatePicker />
                </Form.Item>
              </Col>
              <Col>
                <InputStr
                  type="id"
                  innputNumberProps={{ className: 'filter-form__item' }}
                />
              </Col>
            </Row>
            <Form.Item
              label={t('controls', 'Управление')}
              className="filter-form__item filter-form__buttons-wrapper"
            >
              <Button type="primary" className="filter-form__button" htmlType="submit">
                {t('apply', 'Применить')}
              </Button>
              <Button
                className="filter-form__button"
                onClick={handleResetClick}
              >
                {t('reset', 'Сбросить')}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Collapsed>
  );
};
